import React, { useEffect } from 'react'

import { IframeWindowContent } from 'shared/types/index'
import { getWindowIdFromIframeId } from 'shared/utils/generateWindowIframeId'

import { useWindowStore } from '../../../../../stores'
import { useGetWindowUrl } from '../components/WindowControls/useWindowUrl'

export const IframeNavigationHandler: React.FC = () => {
  const { windows, addToWindowHistory, removeFromWindowHistory } =
    useWindowStore()

  const getWindowUrl = useGetWindowUrl()

  /* Handle iframe‑to‑parent nav (IFRAME_NAV) */
  useEffect(() => {
    const onUnload = (e: MessageEvent) => {
      if (e.data?.type !== 'IFRAME_UNLOAD') return
      const { iframeId } = e.data as { iframeId: string }
      const iframe = document.getElementById(
        iframeId
      ) as HTMLIFrameElement | null

      const windowId = getWindowIdFromIframeId(iframeId)
      const targetWindow = windows.find(w => w.id === windowId)

      const windowUrl = getWindowUrl(windowId)
      if (!windowId || !targetWindow || !windowUrl || !iframe) return

      iframe.src = windowUrl
    }

    const onNavigation = (e: MessageEvent) => {
      const { iframeId, route } = e.data as { iframeId: string; route: string }
      const windowId = getWindowIdFromIframeId(iframeId)
      const targetWindow = windows.find(w => w.id === windowId)

      if (!windowId || !route || !targetWindow) return
      if (targetWindow?.history?.at(-1) === route) return // ignore duplicates

      addToWindowHistory(windowId, route)
    }

    const onMessage = (e: MessageEvent) => {
      switch (e.data?.type) {
        case 'IFRAME_NAV':
          onNavigation(e)
          break
        case 'IFRAME_UNLOAD':
          onUnload(e)
          break
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [addToWindowHistory, getWindowUrl, removeFromWindowHistory, windows])

  /* Handle parent‑to‑iframe nav (Back/Forward) */
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      // Only entries this handler pushed carry iframeId; Back between container
      // routes pops React Router's own state (or null).
      const { iframeId, route } = (e.state ?? {}) as {
        iframeId?: string
        route?: string
      }
      if (!iframeId || !route) return

      const windowId = getWindowIdFromIframeId(iframeId)
      const targetWindow = windows.find(w => w.id === windowId) as
        | IframeWindowContent
        | undefined

      const iframe = document.getElementById(
        iframeId
      ) as HTMLIFrameElement | null
      if (!iframe?.contentWindow) return

      const iframeSrc = iframe.src || iframe.getAttribute('src') || ''
      if (!iframeSrc) return
      let targetOrigin: string
      try {
        targetOrigin = new URL(iframeSrc, window.location.href).origin
      } catch {
        return
      }

      iframe.contentWindow.postMessage(
        {
          type: 'PARENT_NAV_TO_IFRAME',
          route: targetWindow?.history?.at(-2) || route
        },
        targetOrigin
      )
      removeFromWindowHistory(windowId, 2)
    }

    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [removeFromWindowHistory, windows])

  return null
}
